import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LogIn, Home } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      login(response.data);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F2F6FA] px-4">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-blue-700 hover:text-blue-500 transition-colors">
        <Home size={18} />
        <span className="text-sm font-medium">Back to Home</span>
      </Link>
      <div className="bg-white border border-slate-200 rounded-lg p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src="/assets/seedsynch-logo-lockup-dark.svg" 
              alt="SeedSynch - Smarter connections, earlier." 
              className="h-12 sm:h-14 mx-auto"
            />
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="text-blue-800 text-sm font-medium mb-2 block">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="input-field"
              placeholder="your@email.com"
              required
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-blue-800 text-sm font-medium mb-2 block">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="input-field"
              placeholder="••••••••"
              required
            />
          </div>

          <Button
            type="submit"
            className="btn-primary w-full rounded-md py-6"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? 'Logging in...' : (
              <span className="flex items-center justify-center gap-2">
                <LogIn size={18} />
                Login
              </span>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-blue-700 text-sm">
            Don't have an account?{' '}
            <Link to="/register" className="text-blue-600 hover:text-blue-500 font-medium" data-testid="register-link">
              Register
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
